import React, { useEffect, useState } from 'react';
import css from './field-search-for-mapping.module.css';
import Field from '../../../../../../interfaces/field';
import { FIELD_STORED, useTable } from './use-synchronize';
import { FieldForSync, useFieldsForMapping } from './synchronize-context';
import Button from './button';
import { nanoid } from '@reduxjs/toolkit';
import Logo from './logo';
import Edit from './edit';
import { useElementHeight } from '../../../../../../hooks/use-browser';
import Spinner from '../../../../../../components/common/spinner';

export default function FieldSearchForMapping() {
  const { t, tp, farmId, season, fieldService } = useTable<Field>();
  const [state, setState] = useState<FieldForSync[]>([]);
  const {
    setCurrentLadStatusWithLadState,
    mappingState,
    setFieldSearchForMappingState,
    fieldSearchForMappingState: { checked },
    currentLadStatusWithLadState,
    setMappingState,
  } = useFieldsForMapping();
  const [inputState, setInputState] = useState('');
  const [loading, setLoading] = useState(true);
  const [height, elementRef] = useElementHeight<HTMLDivElement>();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (mappingState.checked.length) {
      setTimeout(() => {
        setAnimate(true);
      }, 0);
    } else {
      setAnimate(false);
    }
  }, [mappingState.checked.length]);

  useEffect(() => {
    setFieldSearchForMappingState((prevState) => ({
      ...prevState,
      height,
    }));
  }, [height, setFieldSearchForMappingState]);

  useEffect(() => {
    const subscription = fieldService
      .storedWithGeo(farmId, season)
      .subscribe((response) => {
        setState(response.data);
        setLoading(false);
      });

    return () => {
      subscription.unsubscribe();
      setLoading(true);
    };
  }, [fieldService, farmId, season]);

  useEffect(() => {
    setFieldSearchForMappingState((prevState) => ({
      ...prevState,
      height,
    }));
  }, [height, setFieldSearchForMappingState]);

  const onMouseEnter = (row: FieldForSync) => {
    setFieldSearchForMappingState((prevState) => ({
      ...prevState,
      hovered: row,
    }));
  };

  const onMouseLeave = () => {
    setFieldSearchForMappingState((prevState) => ({
      ...prevState,
      hovered: null,
    }));
  };

  const onCheckboxChange = (field: FieldForSync) => {
    if (checked.some(({ id }) => id === field.id)) {
      setFieldSearchForMappingState((prevState) => ({
        ...prevState,
        checked: prevState.checked.filter(({ id }) => id !== field.id),
      }));
    } else {
      setFieldSearchForMappingState((prevState) => ({
        ...prevState,
        checked: [...prevState.checked, field],
      }));
    }
  };

  const onButtonClick = () => {
    const alreadyMapped = checked
      .map((field) => field.geometry)
      .every((geometry) =>
        mappingState.state
          .filter(
            ({ group_id, stored }) =>
              mappingState.checked[0].group_id === group_id &&
              stored !== FIELD_STORED.LAD
          )
          .map((field) => field.lad_number)
          .includes(geometry)
      );

    if (alreadyMapped) {
      setFieldSearchForMappingState((prevState) => ({
        ...prevState,
        checked: [],
      }));

      setMappingState((prevState) => ({
        ...prevState,
        checked: [],
      }));

      return;
    }

    if (checked.length) {
      const groupId = nanoid();

      const clearMappingStateFromOldFields = mappingState.state.filter(
        (field) => mappingState.checked[0].group_id !== field.group_id
      );

      const checkedFields = checked.map((field, index) => ({
        ...field,
        key: nanoid(),
        stored: <Logo />,
        first_local_field: index === 0,
        last_local_field: index === checked.length - 1,
        editable: index === 0 ? <Edit /> : '',
        group_id: groupId,
      }));

      if (
        (mappingState.checked.length === 1 && checked.length > 1) ||
        (checked.length === 1 && mappingState.checked.length === 1)
      ) {
        const mappingTableCheckedField = {
          ...mappingState.checked[0],
          isCheckable: false,
          group_id: groupId,
        };

        setMappingState((prevState) => ({
          ...prevState,
          state: [
            ...clearMappingStateFromOldFields,
            ...[mappingTableCheckedField, ...checkedFields],
          ],
        }));
      } else if (checked.length === 1 && mappingState.checked.length > 1) {
        const filteredMappingStateCheckedFields = mappingState.checked.map(
          (field) => ({
            ...field,
            isCheckable: false,
            group_id: groupId,
          })
        );
        setMappingState((prevState) => ({
          ...prevState,
          state: [
            ...clearMappingStateFromOldFields,
            ...[...filteredMappingStateCheckedFields, checkedFields[0]],
          ],
        }));
      } else if (checked.length === 1 && mappingState.checked.length === 1) {
        const mappingTableCheckedField = {
          ...mappingState.checked[0],
          isCheckable: false,
          group_id: groupId,
        };

        setMappingState((prevState) => ({
          ...prevState,
          state: [
            ...clearMappingStateFromOldFields,
            ...[mappingTableCheckedField, ...checkedFields],
          ],
        }));
      }
    }

    return;
  };

  const onResetBtnClick = () => {
    setMappingState((prevState) => ({
      ...prevState,
      state: prevState.state.filter(
        ({ group_id }) => group_id !== mappingState.checked[0].group_id
      ),
      checked: [],
      hovered: null,
    }));

    setFieldSearchForMappingState((prevState) => ({
      ...prevState,
      checked: [],
      hovered: null,
    }));

    setCurrentLadStatusWithLadState((prevState) => ({
      ...prevState,
      checked: [],
      hovered: null,
    }));
  };

  const fieldsWithoutAlreadyMappedFields = [
    currentLadStatusWithLadState,
  ].reduce((prevState, currentState) => {
    return prevState.filter(
      ({ geometry, id }) =>
        !currentState.state.some((field) =>
          field.geometry ? field.geometry === geometry : field.id === id
        )
    );
  }, state);

  const removeMappedFields = fieldsWithoutAlreadyMappedFields.filter(
    ({ id }) =>
      !mappingState.state.some((field) => field.id === id) ||
      checked.some((field) => field.id === id)
  );

  const filteredState = removeMappedFields.filter(
    ({ name, block_number, lad_number }) =>
      [name, block_number, lad_number].some((searchParam) =>
        searchParam
          ?.toLocaleLowerCase()
          .startsWith(inputState.toLocaleLowerCase())
      )
  );

  const fields = (
    <ul className="font-normal text-xs leading-3 text-secondary">
      {filteredState.map((field) => (
        <li
          onMouseEnter={() => onMouseEnter(field)}
          onMouseLeave={onMouseLeave}
          className="px-2 py-3 border-b border-solid border-gray-5 flex justify-start gap-1 items-center hover:bg-gray-5"
          key={field.id}
        >
          <input
            type="checkbox"
            name="checkbox"
            className={css['checkbox']}
            aria-label={t('check')}
            aria-describedby="check-fields"
            checked={checked.some(({ id }) => id === field.id)}
            onChange={() => onCheckboxChange(field)}
          />
          <p>
            {field.lad_number && `${field.lad_number}`}
            {field.name && `, ${field.name}`}
            {field.block_number && `, ${field.block_number}`}
            {field.area && `, ${field.area} ha`}
            {field.crop_value && `, ${field.crop_value}`}
            {field.has_cargos && (
              <>
                , <span className="text-error"> {tp('with_cargo')}</span>
              </>
            )}
          </p>
        </li>
      ))}
    </ul>
  );

  const header = (
    <>
      <Logo />
      <h2
        id="check-fields"
        className="font-semibold text-sm leading-4 text-secondary"
      >
        {tp('check_fields')}
      </h2>
    </>
  );

  const input = (
    <input
      placeholder={t('search')}
      className="border border-solid border-gray-5 py-2 px-4 rounded"
      type="search"
      aria-label={t('search')}
      value={inputState}
      onChange={(e) => setInputState(e.target.value)}
    />
  );

  const isButtonDisabled = !(
    (mappingState.checked.length >= 1 && checked.length === 1) ||
    (mappingState.checked.length === 1 && checked.length >= 1) ||
    (mappingState.checked.length >= 1 &&
      checked.length === 1 &&
      !checked[0].geometry)
  );

  const isButtonDisabledIfCargo =
    mappingState.checked.length === 1
      ? checked.length > 1 && checked.some((field) => field?.has_cargos)
      : checked.some((field) => field?.has_cargos);

  const isDisabled = isButtonDisabled || isButtonDisabledIfCargo;

  const errorMsg = [
    {
      condition: isButtonDisabledIfCargo,
      error: tp('cant_split_or_merge_fields_with_cargo'),
    },
  ];

  const error = () => {
    for (const index in errorMsg) {
      if (errorMsg[index].condition) {
        return errorMsg[index].error;
      }
    }
  };

  const renderErrorMsg = <div className="text-error">{error()}</div>;

  const button = (
    <Button disabled={isDisabled} text={t('add')} onClick={onButtonClick} />
  );

  const isResetButtonDisabled = !mappingState.checked[0]?.group_id;

  const resetBtn = (
    <Button
      disabled={isResetButtonDisabled}
      text={t('reset')}
      onClick={onResetBtnClick}
    />
  );

  const renderFields = loading ? (
    <div className="flex justify-center items-center p-4 h-[144px]">
      <Spinner visible={true} size="lg" />
    </div>
  ) : (
    <div className="overflow-y-auto max-h-[144px]">{fields}</div>
  );

  const renderSearchBox = mappingState.checked.length ? (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex justify-start items-center gap-4">{header}</div>
      {input}
      {renderFields}
      {renderErrorMsg}
      <div className="flex justify-end gap-4 items-center flex-wrap">
        <div>{resetBtn}</div>
        <div>{button}</div>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={elementRef}
      className={`h-0 md:transition-none transition-all md:absolute md:top-8 md:z-50 bg-white p-4 border border-solid border-gray-6 md:w-11/12 w-full rounded
      ${css['shadow']}
      ${mappingState.checked.length === 0 ? 'hidden' : 'md:!h-fit'}
      ${animate && '!h-full'}`}
    >
      {renderSearchBox}
    </div>
  );
}
